/**
 * @name ModalConfirmacaoPix
 * @author Gabriel M. Guimarães | gabrielmg7
 * @description Modal de confirmação antes de gerar PIX para recarga
 * @package components/pages/gestao-financeira/creditos/components
 */

import { Info as InfoIcon, Pix as PixIcon, Warning as WarningIcon } from '@mui/icons-material';
import { Alert, Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, Divider, Stack, Typography } from '@mui/material';
import React from 'react';

interface ModalConfirmacaoPixProps {
     open: boolean;
     onClose: () => void;
     onConfirm: () => void;
     valor: number;
     descricao: string;
     loading?: boolean;
}

/**
 * Modal de confirmação antes de gerar PIX
 * Exibe informações da transação e aviso sobre processo de pagamento
 */
const ModalConfirmacaoPix: React.FC<ModalConfirmacaoPixProps> = ({ open, onClose, onConfirm, valor, descricao, loading = false }) => {
     const formatCurrency = (value: number) => {
          return new Intl.NumberFormat('pt-BR', {
               style: 'currency',
               currency: 'BRL',
          }).format(value);
     };

     return (
          <Dialog
               open={open}
               onClose={onClose}
               maxWidth='sm'
               fullWidth
               PaperProps={{ sx: { borderRadius: 2 } }}
          >
               <DialogTitle sx={{ pb: 1 }}>
                    <Typography
                         variant='h6'
                         sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
                    >
                         <PixIcon color='primary' />
                         Confirmar Geração de PIX
                    </Typography>
               </DialogTitle>

               <DialogContent>
                    <Stack spacing={3}>
                         {/* Resumo da Transação */}
                         <Box
                              sx={{
                                   p: 2,
                                   bgcolor: 'primary.50',
                                   borderRadius: 1,
                                   border: '1px solid',
                                   borderColor: 'primary.200',
                              }}
                         >
                              <Typography
                                   variant='subtitle2'
                                   color='primary.main'
                                   gutterBottom
                              >
                                   Resumo da Recarga
                              </Typography>
                              <Typography
                                   variant='h5'
                                   color='primary.main'
                                   fontWeight='bold'
                              >
                                   {formatCurrency(valor)}
                              </Typography>
                              <Typography
                                   variant='body2'
                                   color='text.secondary'
                                   sx={{ mt: 1 }}
                              >
                                   {descricao}
                              </Typography>
                         </Box>

                         <Divider />

                         {/* Informações sobre o Processo */}
                         <Alert
                              severity='info'
                              icon={<InfoIcon />}
                         >
                              <Typography
                                   variant='body2'
                                   fontWeight='medium'
                                   gutterBottom
                              >
                                   Como funciona o processo:
                              </Typography>
                              <Typography
                                   variant='body2'
                                   component='div'
                              >
                                   1. Será gerado um código PIX exclusivo para você
                                   <br />
                                   2. Você será redirecionado para o app do seu banco
                                   <br />
                                   3. Após o pagamento, os créditos serão creditados automaticamente
                                   <br />
                                   4. Você receberá uma notificação quando os créditos estiverem disponíveis
                              </Typography>
                         </Alert>

                         {/* Aviso Importante */}
                         <Alert
                              severity='warning'
                              icon={<WarningIcon />}
                         >
                              <Typography
                                   variant='body2'
                                   fontWeight='medium'
                                   gutterBottom
                              >
                                   Atenção:
                              </Typography>
                              <Typography variant='body2'>
                                   • Não feche esta aba até completar o pagamento
                                   <br />
                                   • O PIX tem validade de 30 minutos
                                   <br />• Em caso de problemas, entre em contato com o suporte
                              </Typography>
                         </Alert>

                         {/* Informações Técnicas */}
                         <Box
                              sx={{
                                   p: 2,
                                   bgcolor: 'grey.50',
                                   borderRadius: 1,
                                   fontSize: '0.75rem',
                                   color: 'text.secondary',
                              }}
                         >
                              <Typography
                                   variant='caption'
                                   display='block'
                              >
                                   🔒 Transação segura processada via Banco do Brasil
                              </Typography>
                              <Typography
                                   variant='caption'
                                   display='block'
                              >
                                   💳 Método de pagamento: PIX Instantâneo
                              </Typography>
                              <Typography
                                   variant='caption'
                                   display='block'
                              >
                                   ⏱️ Liberação do crédito: em até 30 minutos
                              </Typography>
                         </Box>
                    </Stack>
               </DialogContent>

               <DialogActions sx={{ p: 3, pt: 2 }}>
                    <Button
                         onClick={onClose}
                         disabled={loading}
                         color='inherit'
                    >
                         Cancelar
                    </Button>
                    <Button
                         onClick={onConfirm}
                         variant='contained'
                         disabled={loading}
                         startIcon={<PixIcon />}
                         size='large'
                    >
                         {loading ? 'Gerando PIX...' : 'Gerar PIX'}
                    </Button>
               </DialogActions>
          </Dialog>
     );
};

export default ModalConfirmacaoPix;
